import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import StandardModal from "../../common/Modal/StandardModal";
import SelectDropdown from "../../library/SelectDropdown";
import { ErrorText, FieldWrapper, HelperText, Label } from "../../library/DateInput";
import Checkbox from "../../library/Checkbox";
import Button from "../../library/Button";
import ButtonGroup from "../../library/ButtonGroup";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import { UpdateOhsBuildingRepresentatives } from "../../../services/FirstAidBoxService";
import {
    invalidateFirstAidBoxQueries,
    useOhsAccess,
    useOhsBuildingCheckFilters,
    useOhsBuildingCheckList
} from "../../../query/hooks/QueryHooksFirstAidBox";
import { OHS_BASE_PATH, PERFORM_BUILDING_CHECK_TOOLTIP } from "./ohsConstants";
import { getOhsErrorMessage, getPrefixedUser } from "./ohsUtils";
import { CheckboxList, ConditionBadge, MutedText } from "./Ohs.styles";
import OhsUserCell from "./OhsUserCell";

const EMPTY_FILTER = { arr: [], checkObj: {} };
// Search-style filters keep their ticked options in an array, not an object.
const EMPTY_SEARCH_FILTER = { arr: [], checkObj: [] };
const EXPORT_HEADER = ["Campus", "Building", "OHS Representatives", "Last Checked", "Last Checked By", "Last Result", "This Month"];

/**
 * A building's latest check result in words, e.g. "2 issues" or "All clear".
 * @param {Object} building Building Checks row.
 * @returns {string}
 */
function describeLastResult(building) {
    if (!building.last_check_id) {
        return "Not yet checked";
    }
    if (building.last_issue_count > 0) {
        return `${building.last_issue_count} ${building.last_issue_count === 1 ? "issue" : "issues"}`;
    }
    return "All clear";
}

/**
 * Building Checks tab - every campus building with OHS Representatives assigned, who they are, and
 * whether this month's check is done. Auto-filtered to the user's own buildings when they have any.
 * Adding a building or changing its reps needs the OHS Management permission.
 */
function BuildingCheckList() {
    const history = useHistory();
    const { data: ohsAccess } = useOhsAccess();
    const { data: buildingChecks, isLoading } = useOhsBuildingCheckList();
    const { data: buildingCheckFilters } = useOhsBuildingCheckFilters();
    const [search, setSearch] = useState("");
    const [campusFilter, setCampusFilter] = useState(EMPTY_FILTER);
    const [buildingFilter, setBuildingFilter] = useState(EMPTY_SEARCH_FILTER);
    const [representativeFilter, setRepresentativeFilter] = useState(EMPTY_FILTER);
    const [checkStatusFilter, setCheckStatusFilter] = useState(EMPTY_FILTER);
    const [myBuildingsOnly, setMyBuildingsOnly] = useState(false);
    const [hasAppliedDefault, setHasAppliedDefault] = useState(false);
    // { buildingId, buildingName, ohsRepresentativeIds, isNewBuilding } while the assign popup is open.
    const [assigningBuilding, setAssigningBuilding] = useState(null);
    const [assignmentErrors, setAssignmentErrors] = useState({});
    const [isSavingAssignment, setIsSavingAssignment] = useState(false);

    useEffect(() => {
        if (hasAppliedDefault || buildingCheckFilters.hasAssignedBuildings === undefined) {
            return;
        }
        setMyBuildingsOnly(buildingCheckFilters.hasAssignedBuildings);
        setHasAppliedDefault(true);
    }, [buildingCheckFilters.hasAssignedBuildings, hasAppliedDefault]);

    const filteredBuildingChecks = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        return buildingChecks
            .filter((building) => !myBuildingsOnly || building.is_my_building)
            .filter((building) => !campusFilter.arr.length || campusFilter.arr.includes(String(building.campus_id)))
            .filter((building) => !buildingFilter.arr.length || buildingFilter.arr.map(String).includes(String(building.building_id)))
            .filter((building) => {
                return !representativeFilter.arr.length
                    || building.ohs_representative_ids.some((representativeId) => representativeFilter.arr.includes(String(representativeId)));
            })
            .filter((building) => {
                // Both or neither selected means no filter.
                if (checkStatusFilter.arr.length !== 1) {
                    return true;
                }
                return checkStatusFilter.arr[0] === "checked" ? building.is_checked_this_month : !building.is_checked_this_month;
            })
            .filter((building) => {
                return !searchText
                    || (building.building_name || "").toLowerCase().includes(searchText)
                    || (building.campus_name || "").toLowerCase().includes(searchText)
                    || building.ohs_representative_names.toLowerCase().includes(searchText);
            });
    }, [buildingChecks, search, myBuildingsOnly, campusFilter, buildingFilter, representativeFilter, checkStatusFilter]);

    // Add Building only offers buildings that aren't on the list yet.
    const unlistedBuildingOptions = useMemo(() => {
        const listedBuildingIds = new Set(buildingChecks.map((building) => Number(building.building_id)));
        return (buildingCheckFilters.BuildingOptions || []).filter((buildingOption) => !listedBuildingIds.has(Number(buildingOption.value)));
    }, [buildingChecks, buildingCheckFilters.BuildingOptions]);

    const openAssignment = (assignment) => {
        setAssignmentErrors({});
        setAssigningBuilding(assignment);
    };

    const toggleRepresentative = (representativeId, isChecked) => {
        setAssigningBuilding((currentBuilding) => ({
            ...currentBuilding,
            ohsRepresentativeIds: isChecked
                ? [...currentBuilding.ohsRepresentativeIds, representativeId]
                : currentBuilding.ohsRepresentativeIds.filter((selectedId) => selectedId !== representativeId),
        }));
        setAssignmentErrors((currentErrors) => ({ ...currentErrors, ohsRepresentativeIds: undefined }));
    };

    const handleSaveAssignment = async () => {
        const requiredErrors = {
            buildingId: assigningBuilding.buildingId ? undefined : "Choose a building",
            ohsRepresentativeIds: !assigningBuilding.isNewBuilding || assigningBuilding.ohsRepresentativeIds.length ? undefined : "Choose at least one OHS Representative",
        };
        if (Object.values(requiredErrors).some(Boolean)) {
            setAssignmentErrors(requiredErrors);
            return;
        }

        try {
            if (!assigningBuilding.ohsRepresentativeIds.length) {
                const confirmation = await Swal.fire({
                    title: "Are you sure?",
                    text: `With no OHS Representatives, ${assigningBuilding.buildingName} comes off the Building Checks list. Its check history is kept.`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "var(--button-color)",
                    cancelButtonColor: "var(--states-alert)",
                    confirmButtonText: "Yes, remove it",
                });
                if (!confirmation.isConfirmed) {
                    return;
                }
            }

            setIsSavingAssignment(true);
            await UpdateOhsBuildingRepresentatives(assigningBuilding.buildingId, assigningBuilding.ohsRepresentativeIds);
            await invalidateFirstAidBoxQueries();
            setAssigningBuilding(null);
        } catch (error) {
            console.log("BuildingCheckList.js->handleSaveAssignment->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The OHS Representatives could not be saved."), "error");
        } finally {
            setIsSavingAssignment(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                name: "Building",
                selector: "building_name",
                sortable: true,
                minWidth: "180px",
                cell: (row) => (
                    <div>
                        <div className="textLimit100" title={row.building_name}>{row.building_name}</div>
                        <MutedText>{row.campus_name}</MutedText>
                    </div>
                ),
            },
            {
                name: "OHS Representatives",
                selector: "ohs_representative_names",
                sortable: true,
                grow: 2,
                minWidth: "220px",
                cell: (row) => (
                    <div>
                        {row.ohs_representatives.map((ohsRepresentative) => (
                            <OhsUserCell key={ohsRepresentative.user_id} user={ohsRepresentative} />
                        ))}
                    </div>
                ),
            },
            {
                name: "Last Checked",
                selector: "last_checked_at",
                sortable: true,
                minWidth: "200px",
                cell: (row) => {
                    if (!row.last_check_id) {
                        return <MutedText>Never</MutedText>;
                    }
                    return (
                        <div>
                            <div>{moment(row.last_checked_at).format(TABLE_DATE_FORMAT)}</div>
                            <OhsUserCell user={getPrefixedUser(row, "checked_by", row.last_checked_by_id)} />
                        </div>
                    );
                },
            },
            {
                name: "Last Result",
                selector: "last_issue_count",
                sortable: true,
                minWidth: "130px",
                cell: (row) => {
                    if (!row.last_check_id) {
                        return "-";
                    }
                    return <ConditionBadge $condition={row.last_issue_count > 0 ? "damaged" : "ok"}>{describeLastResult(row)}</ConditionBadge>;
                },
            },
            {
                name: "This Month",
                selector: "is_checked_this_month",
                sortable: true,
                minWidth: "120px",
                cell: (row) => (
                    <ConditionBadge $condition={row.is_checked_this_month ? "ok" : "low"}>
                        {row.is_checked_this_month ? "Done" : "Due"}
                    </ConditionBadge>
                ),
            },
            {
                name: "Action",
                width: "110px",
                right: true,
                cell: (row) => (
                    <div className="assessment-08 btn-dropdown-grp">
                        <div className="as-buttons">
                            <button
                                type="button"
                                className="btn btn-primary rounded-circle"
                                title={PERFORM_BUILDING_CHECK_TOOLTIP}
                                onClick={() => history.push(`${OHS_BASE_PATH}/buildingChecks/check/${row.building_id}`)}
                            >
                                <i className="fal fa-clipboard-check"></i>
                            </button>
                            {ohsAccess.canManagePeople && (
                                <button
                                    type="button"
                                    className="btn btn-primary rounded-circle"
                                    title="Edit OHS Representatives"
                                    onClick={() => openAssignment({
                                        buildingId: row.building_id,
                                        buildingName: row.building_name,
                                        ohsRepresentativeIds: row.ohs_representative_ids,
                                        isNewBuilding: false,
                                    })}
                                >
                                    <i className="fal fa-user-edit"></i>
                                </button>
                            )}
                        </div>
                    </div>
                ),
            },
        ],
        [history, ohsAccess.canManagePeople]
    );

    /**
     * Rows for the CSV/XLSX/PDF export - the buildings currently shown.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = filteredBuildingChecks.map((building) => [
            building.campus_name,
            building.building_name,
            building.ohs_representative_names,
            building.last_check_id ? moment(building.last_checked_at).format(TABLE_DATE_FORMAT) : "Never",
            building.last_checked_by_name || "",
            describeLastResult(building),
            building.is_checked_this_month ? "Done" : "Due",
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <>
            <DataTableComponentFrontPagination
                data={filteredBuildingChecks}
                columns={columns}
                loading={isLoading}
                keyField="building_id"
                search={search}
                setSearch={setSearch}
                exportData={exportData}
                exportFileName="OHS_Building_Checks"
                defaultSort={{ defaultSortColumn: "building_name", defaultSortAsc: true }}
                filters={[
                    {
                        filterName: "Campus",
                        optionArr: buildingCheckFilters.CampusFilter || [],
                        state: campusFilter,
                        setState: setCampusFilter,
                        uniqueId: "ohsBuildingCheckCampus",
                    },
                    {
                        filterName: "Building",
                        optionArr: buildingCheckFilters.BuildingFilter || [],
                        state: buildingFilter,
                        setState: setBuildingFilter,
                        uniqueId: "ohsBuildingCheckBuilding",
                        isSearchFilter: true,
                    },
                    {
                        filterName: "OHS Representative",
                        optionArr: buildingCheckFilters.OhsRepresentativeFilter || [],
                        state: representativeFilter,
                        setState: setRepresentativeFilter,
                        uniqueId: "ohsBuildingCheckRepresentative",
                    },
                    {
                        filterName: "This Month",
                        optionArr: buildingCheckFilters.CheckStatusFilter || [],
                        state: checkStatusFilter,
                        setState: setCheckStatusFilter,
                        uniqueId: "ohsBuildingCheckStatus",
                    },
                ]}
                tableButton={[
                    <ButtonGroup key="building-check-actions">
                        {buildingCheckFilters.hasAssignedBuildings && (
                            <Button
                                variant={myBuildingsOnly ? "primary" : "secondary"}
                                onClick={() => setMyBuildingsOnly((currentValue) => !currentValue)}
                                title={myBuildingsOnly ? "Showing buildings you are an OHS Representative for" : "Showing every building"}
                            >
                                <i className="fal fa-user-check"></i>
                                {myBuildingsOnly ? "My Buildings Only" : "All Buildings"}
                            </Button>
                        )}
                        <Button
                            onClick={() => history.push(`${OHS_BASE_PATH}/buildingChecks/checklist`)}
                            title="Add, edit or delete the questions on the building checklist"
                        >
                            <i className="fal fa-list-ul"></i>
                            Edit Checklist
                        </Button>
                        {ohsAccess.canManagePeople && (
                            <Button
                                onClick={() => openAssignment({ buildingId: "", buildingName: "", ohsRepresentativeIds: [], isNewBuilding: true })}
                                title="Add a building to the monthly checks by assigning its OHS Representatives"
                            >
                                <i className="fal fa-plus"></i>
                                Add Building
                            </Button>
                        )}
                    </ButtonGroup>,
                ]}
            />
            <StandardModal
                show={Boolean(assigningBuilding)}
                onHide={() => setAssigningBuilding(null)}
                title={assigningBuilding?.isNewBuilding ? "Add Building" : `OHS Representatives - ${assigningBuilding?.buildingName || ""}`}
                icon={<i className={assigningBuilding?.isNewBuilding ? "fal fa-plus" : "fal fa-user-edit"} />}
                onSave={handleSaveAssignment}
                saveLoading={isSavingAssignment}
            >
                {assigningBuilding?.isNewBuilding && (
                    <SelectDropdown
                        id="ohsAssignBuilding"
                        label="Building"
                        required
                        options={unlistedBuildingOptions}
                        value={assigningBuilding.buildingId}
                        onChange={(event) => {
                            const chosenBuilding = unlistedBuildingOptions.find((buildingOption) => buildingOption.value === event.target.value);
                            setAssigningBuilding((currentBuilding) => ({ ...currentBuilding, buildingId: event.target.value, buildingName: chosenBuilding?.label || "" }));
                            setAssignmentErrors((currentErrors) => ({ ...currentErrors, buildingId: undefined }));
                        }}
                        errorText={assignmentErrors.buildingId}
                        helperText="Campus buildings come from Campus Management."
                    />
                )}
                <FieldWrapper className="mt-3">
                    <Label data-required={assigningBuilding?.isNewBuilding ? "true" : undefined}>OHS Representatives</Label>
                    <CheckboxList>
                        {(buildingCheckFilters.OhsRepresentativeFilter || []).map((representativeOption) => (
                            <Checkbox
                                key={representativeOption.value}
                                id={`ohsAssignRepresentative${representativeOption.value}`}
                                size="md"
                                label={representativeOption.label}
                                checked={Boolean(assigningBuilding?.ohsRepresentativeIds.includes(Number(representativeOption.value)))}
                                onChange={(event) => toggleRepresentative(Number(representativeOption.value), event.target.checked)}
                            />
                        ))}
                    </CheckboxList>
                    {assignmentErrors.ohsRepresentativeIds
                        ? <ErrorText>{assignmentErrors.ohsRepresentativeIds}</ErrorText>
                        : <HelperText>Responsible for this building&apos;s monthly check. Only people on the OHS Representatives list can be assigned.</HelperText>}
                </FieldWrapper>
            </StandardModal>
        </>
    );
}

export default BuildingCheckList;
